import React, { useMemo } from 'react';
import {
  Map as LeafletMap,
  TileLayer,
  Marker as RLMarker,
  Popup,
  Polyline,
  LayerGroup,
  ZoomControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix pro defaultní ikony markerů ve Webpack/CRA
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

/* ================== Pomocné funkce ================== */

// seconds/ISO → sekundy
function toTimeSec(t) {
  if (t == null) return null;
  if (Number.isFinite(t)) return t; // už je to v sekundách
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms / 1000 : null;
}

// Haversine vzdálenost v metrech
function distanceM(a, b) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δφ = toRad(b.lat - a.lat);
  const Δλ = toRad(b.lng - a.lng);
  const s =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// HSL → HEX (pro plynulé barvy podle rychlosti)
function hslToHex(h, s, l) {
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.round(255 * c);
  };
  const r = f(0),
    g = f(8),
    b = f(4);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

// Mapování rychlosti (km/h) → barva (červená→žlutá→zelená)
function speedToColor(kmh, minKmh = 5, maxKmh = 20) {
  if (!Number.isFinite(kmh)) return '#3388ff';
  const t = Math.max(
    0,
    Math.min(1, (kmh - minKmh) / Math.max(1e-6, maxKmh - minKmh)),
  );
  const hue = 0 + t * 120; // 0=red .. 120=green
  return hslToHex(hue, 1, 0.5);
}

/* =============== Vyhlazení a zahuštění =============== */

// Exponential Moving Average: vyhladí lat/lng, zachová ostatní pole bodu
function emaSmooth(points, alpha = 0.25) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const out = new Array(points.length);
  out[0] = { ...points[0] };
  for (let i = 1; i < points.length; i++) {
    const p = points[i],
      prev = out[i - 1];
    out[i] = {
      ...p,
      lat: prev.lat + alpha * (p.lat - prev.lat),
      lng: prev.lng + alpha * (p.lng - prev.lng),
    };
  }
  return out;
}

function catmullRomPoint(p0, p1, p2, p3, t) {
  const t2 = t * t,
    t3 = t2 * t;
  const cr = (a0, a1, a2, a3) =>
    0.5 *
    (2 * a1 +
      (-a0 + a2) * t +
      (2 * a0 - 5 * a1 + 4 * a2 - a3) * t2 +
      (-a0 + 3 * a1 - 3 * a2 + a3) * t3);
  return {
    lat: cr(p0.lat, p1.lat, p2.lat, p3.lat),
    lng: cr(p0.lng, p1.lng, p2.lng, p3.lng),
  };
}

/** Vytvoří segmenty s barvou dle rychlosti, volitelně vyhladí a zahuští křivku.
 * Priorita rychlosti:
 *  1) průměr `speedKmh` ze sousedních originálních bodů (pokud k dispozici)
 *  2) výpočet z originálních časů a vzdálenosti (pro přesnost)
 * Vyhlazení a zahuštění se používá jen pro geometrii vykreslení.
 */
function useColoredSegments(
  points,
  { minKmh = 5, maxKmh = 20, smooth = null } = {},
) {
  return useMemo(() => {
    if (!Array.isArray(points) || points.length < 2) return [];
    // 1) Vyhlazení EMA pozice (ne času/rychlosti)
    const alpha = Number.isFinite(smooth?.alpha) ? smooth.alpha : null;
    const basePts = Number.isFinite(alpha) ? emaSmooth(points, alpha) : points;

    // 2) Počet vzorků na segment pro Catmull-Rom (>=1)
    const samplesPerSeg = Math.max(1, Math.floor(smooth?.samplesPerSeg || 1));

    const segs = [];
    for (let i = 1; i < basePts.length; i++) {
      const p1 = basePts[i - 1];
      const p2 = basePts[i];
      if (
        !p1 ||
        !p2 ||
        !Number.isFinite(p1.lat) ||
        !Number.isFinite(p1.lng) ||
        !Number.isFinite(p2.lat) ||
        !Number.isFinite(p2.lng)
      )
        continue;

      // rychlost z ORIGINÁLNÍCH (nesmooth) bodů kvůli přesnosti
      const o1 = points[i - 1];
      const o2 = points[i];

      let kmh =
        Number.isFinite(o1?.speedKmh) && Number.isFinite(o2?.speedKmh)
          ? (o1.speedKmh + o2.speedKmh) / 2
          : null;

      if (!Number.isFinite(kmh)) {
        const t1 = toTimeSec(o1?.time);
        const t2s = toTimeSec(o2?.time);
        if (Number.isFinite(t1) && Number.isFinite(t2s) && t2s > t1) {
          const dist = distanceM(o1, o2); // m
          const dt = t2s - t1; // s
          kmh = (dist / dt) * 3.6;
        }
      }
      if (Number.isFinite(kmh)) kmh = Math.round(kmh * 100) / 100;

      if (samplesPerSeg === 1) {
        // Bez zahuštění
        segs.push({
          positions: [
            [p1.lat, p1.lng],
            [p2.lat, p2.lng],
          ],
          color: speedToColor(kmh, minKmh, maxKmh),
          speedKmh: Number.isFinite(kmh) ? kmh : null,
        });
      } else {
        // Catmull-Rom zahuštění pro plynulejší křivku
        const p0 = basePts[i - 2] || p1;
        const p3 = basePts[i + 1] || p2;
        let prev = p1;
        for (let s = 1; s <= samplesPerSeg; s++) {
          const t = s / samplesPerSeg;
          const cur = catmullRomPoint(p0, p1, p2, p3, t);
          segs.push({
            positions: [
              [prev.lat, prev.lng],
              [cur.lat, cur.lng],
            ],
            color: speedToColor(kmh, minKmh, maxKmh),
            speedKmh: Number.isFinite(kmh) ? kmh : null,
          });
          prev = cur;
        }
      }
    }
    return segs;
  }, [points, minKmh, maxKmh, smooth]);
}

/* =============== Map komponent =============== */
/**
 * Props:
 *  - apiKey: Mapy.cz API klíč
 *  - center: {lat, lng}
 *  - zoom: číslo (default 13)
 *  - mouse: { zoom, pan, wheel }  // obdoba <MouseControl/>
 *  - marker: { enabled, popup }   // obdoba <MarkerLayer><Marker/></MarkerLayer>
 *  - pathPoints: [{ lat, lng, time?, speedKmh? }, ...] // lze kombinovat GPX čas + API speed
 *  - pathStyle: { weight, opacity }
 *  - speedDomain: { minKmh, maxKmh } // rozsah pro barvení rychlosti
 *  - smooth: { alpha, samplesPerSeg } // vyhlazení a zahuštění křivky
 *  - height: výška mapy v px (default 400)
 */
export const Map = ({
  apiKey,
  center,
  zoom = 13,
  mouse = { zoom: true, pan: true, wheel: false },
  marker = { enabled: true, popup: 'Selected Location' },
  pathPoints = [],
  pathStyle = { weight: 6, opacity: 0.85 },
  speedDomain = { minKmh: 5, maxKmh: 20 },
  smooth = { alpha: 0.25, samplesPerSeg: 6 },
  height = 400,
}) => {
  const segments = useColoredSegments(pathPoints, { ...speedDomain, smooth });

  return (
    <LeafletMap
      center={[center.lat, center.lng]}
      zoom={zoom}
      style={{ height, width: '100%' }}
      keyboard={true}
      scrollWheelZoom={!!mouse.wheel}
      dragging={!!mouse.pan}
      doubleClickZoom={!!mouse.zoom}
      boxZoom={!!mouse.zoom}
      zoomControl={false}
      preferCanvas={true}
    >
      <ZoomControl position="topright" />

      <TileLayer
        url={`https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=${apiKey}`}
        attribution='<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz</a>'
        maxZoom={19}
      />

      {marker?.enabled && (
        <LayerGroup>
          <RLMarker position={[center.lat, center.lng]}>
            {marker?.popup ? <Popup>{marker.popup}</Popup> : null}
          </RLMarker>
        </LayerGroup>
      )}

      {segments.length > 0 && (
        <LayerGroup>
          {segments.map((seg, i) => (
            <Polyline
              key={i}
              positions={seg.positions}
              color={seg.color}
              weight={pathStyle.weight ?? 6}
              opacity={pathStyle.opacity ?? 0.85}
            />
          ))}
        </LayerGroup>
      )}
    </LeafletMap>
  );
};

export default Map;

/* ====== Utility: GPX parser + zipování streamů do bodů ====== */

// GPX XML string → [{lat,lng,time}]
export function parseGpxToPoints(gpxString) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(gpxString, 'application/xml');
  const trkpts = Array.from(xml.getElementsByTagName('trkpt'));
  return trkpts.map((el) => ({
    lat: parseFloat(el.getAttribute('lat')),
    lng: parseFloat(el.getAttribute('lon')),
    time: el.getElementsByTagName('time')[0]?.textContent ?? null,
  }));
}

// Sloučení odpovědi z API (path/time/speedKmh) do bodů pro Map
export function zipStreamToPoints({ path = [], time = [], speedKmh = [] }) {
  const n = path.length;
  const pts = new Array(n);
  for (let i = 0; i < n; i++) {
    pts[i] = {
      lat: path[i]?.lat,
      lng: path[i]?.lng,
      time: time[i] ?? null,
      speedKmh: speedKmh[i] ?? null,
    };
  }
  return pts;
}
